export type DayState = 'answered' | 'missed' | 'notDue';

type DayOpts = {
  day: string;
  /** Today in the student's own zone. */
  today: string;
  /** The student's local joining date, absent when the membership has none. */
  startedOn?: string;
  /** The course's standup window. */
  startDate?: string;
  endDate?: string;
};

/**
 * Whether this day was the student's to answer. Every uncertainty resolves to
 * "no": a day is only owed once we know the course was collecting standups
 * that day and the student was already here for it. Marking a day missed
 * accuses someone, so the cost of a wrong "no" is far lower than a wrong yes.
 *
 * Without a standup start date there is no way to tell a day a student skipped
 * from a day before the course ever asked, so nothing is marked missed at all.
 */
export function isDueDay(opts: DayOpts): boolean {
  if (!opts.startDate || !opts.startedOn) {
    return false;
  }
  // Today is still running, and the joining day is not held against anyone.
  if (opts.day >= opts.today || opts.day <= opts.startedOn) {
    return false;
  }
  if (opts.day < opts.startDate) {
    return false;
  }
  if (opts.endDate && opts.day > opts.endDate) {
    return false;
  }
  return true;
}

/**
 * An answered day reads as answered wherever it falls, so a standup submitted
 * outside the window still shows as done.
 */
export function dayState(opts: DayOpts & { answered: boolean }): DayState {
  if (opts.answered) {
    return 'answered';
  }
  return isDueDay(opts) ? 'missed' : 'notDue';
}

export type RowSummary = {
  /** One entry per column, in the order given. */
  cells: Array<{ state: DayState; answered: boolean; due: boolean }>;
  /** Standups posted across the columns shown. */
  answered: number;
  /** Days owed or answered — what `answered` is counted against. */
  due: number;
  /** The days held against the student (owed, answered or not). */
  dueDays: string[];
};

/**
 * One student's row. Every standup they posted counts, against every day that
 * was owed or answered: being careful about what to call missed must not make
 * a posted standup vanish from the count beside it, and every row has to be
 * readable against every other.
 */
export function summariseRow(opts: {
  days: string[];
  answeredDays: string[];
  today: string;
  startedOn?: string;
  startDate?: string;
  endDate?: string;
}): RowSummary {
  const cells = opts.days.map((day) => {
    const answered = opts.answeredDays.includes(day);
    return {
      answered,
      due: isDueDay({ ...opts, day }),
      state: dayState({ ...opts, day, answered }),
    };
  });

  return {
    cells,
    answered: cells.filter((cell) => cell.answered).length,
    due: cells.filter((cell) => cell.due || cell.answered).length,
    dueDays: opts.days.filter((_, i) => cells[i].due),
  };
}
